import React, { useMemo, useCallback, useState } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import dagre from 'dagre';
import 'reactflow/dist/style.css';

// Custom node component for questions, answers, and theses
const QuestionNode = ({ data }) => {
  const { label, questionType, hasLogic, isSelected, logicComplexity, isFromTargetDate, isDependency, isAnswer, isThesis } = data;
  
  const getNodeColor = () => {
    // Special color for thesis nodes
    if (isThesis) {
      return '#f3e5f5'; // Light purple for thesis nodes
    }
    
    // Special color for answer nodes
    if (isAnswer) {
      return '#fff3e0'; // Light orange for answer nodes
    }
    
    // Special colors for date filtering
    if (isFromTargetDate) {
      switch (questionType) {
        case 'Radio': return '#bbdefb';        // Darker blue for target date
        case 'Checkbox': return '#bbdefb';     // Darker blue for target date (changed from purple)
        case 'LongAnswer': return '#c8e6c9';   // Darker green for target date
        case 'Contradiction': return '#ffcdd2'; // Darker red for target date
        default: return '#e0e0e0';             // Darker gray for target date
      }
    } else if (isDependency) {
      // Lighter, muted colors for dependencies
      switch (questionType) {
        case 'Radio': return '#e3f2fd';        // Light blue for dependencies (changed from purple)
        case 'Checkbox': return '#e3f2fd';     // Light blue for dependencies (changed from orange)
        case 'LongAnswer': return '#f1f8e9';   // Light green for dependencies
        case 'Contradiction': return '#fce4ec'; // Light pink for dependencies
        default: return '#fafafa';             // Very light gray for dependencies
      }
    }
    
    // Default colors
    switch (questionType) {
      case 'Radio': return '#e3f2fd';
      case 'Checkbox': return '#e3f2fd';      // Changed from purple to match Radio blue
      case 'LongAnswer': return '#e8f5e8';
      case 'Contradiction': return '#ffebee';
      default: return '#f5f5f5';
    }
  };

  const getBorderColor = () => {
    if (isSelected) return 'var(--color1)';    // Use same color as class day selection
    if (isThesis) return '#9c27b0';             // Purple border for thesis nodes
    if (isAnswer) return '#ff9800';              // Orange border for answer nodes
    if (isFromTargetDate) return '#2196f3';     // Blue border for target date questions
    if (isDependency) return '#ff9800';         // Orange border for dependencies
    if (hasLogic) return '#ff9800';
    return '#ccc';
  };

  const getBorderWidth = () => {
    if (isSelected) return '3px';               // Slightly thicker than class day selection
    if (logicComplexity === 'high') return '3px';
    if (logicComplexity === 'medium') return '2px';
    return '1px';
  };

  return (
    <div 
      style={{
        padding: '12px',
        borderRadius: '8px',
        border: `${getBorderWidth()} solid ${getBorderColor()}`,
        backgroundColor: getNodeColor(),
        minWidth: '180px',
        maxWidth: '220px',
        textAlign: 'center',
        fontSize: '12px',
        fontWeight: 'normal',
        boxShadow: isSelected ? '0 4px 8px rgba(0,0,0,0.2)' : '0 2px 4px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        position: 'relative',
        cursor: isAnswer ? 'default' : 'pointer', // Answer nodes are not clickable
      }}
    >
      {/* Input handle (top) - for dependencies coming into this question */}
      <Handle
        type="target"
        position={Position.Top}
        id="input"
        style={{
          background: '#555',
          width: '10px',
          height: '10px',
          border: '2px solid white',
        }}
      />
      
      {/* Output handle (bottom) - for this question's dependencies going out */}
      <Handle
        type="source"
        position={Position.Bottom}
        id="output"
        style={{
          background: '#555',
          width: '10px',
          height: '10px',
          border: '2px solid white',
        }}
      />
      
      <div style={{ 
        lineHeight: '1.4',
        wordWrap: 'break-word',
        fontSize: '14px' // Increased from 12px
      }}>
        {label}
      </div>
    </div>
  );
};

const nodeTypes = {
  questionNode: QuestionNode,
};

// Dagre layout function
const getLayoutedElements = (nodes, edges, direction = 'TB') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: direction,
    nodesep: 100,  // Reduced horizontal spacing but still enough for labels
    ranksep: 150,  // Reduced vertical spacing while maintaining hierarchy
    marginx: 30,   // Smaller margins
    marginy: 30,
    acyclicer: 'greedy',     // Handle cycles better
    ranker: 'tight-tree',    // More compact tree layout
    edgesep: 15,            // Keep edge separation for clarity
    align: 'UL'             // Upper-left alignment for tighter packing
  });

  console.log('🔧 Dagre layout - INPUT:', { nodes: nodes.length, edges: edges.length });
  console.log('Sample input edges:', edges.slice(0, 3));

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 200, height: 120 }); // Adjusted for tighter spacing
  });

  edges.forEach((edge, index) => {
    console.log(`Adding edge ${index + 1}/${edges.length} to Dagre:`, edge.source, '->', edge.target);
    dagreGraph.setEdge(edge.source, edge.target);
  });

  console.log('🔧 Dagre graph edges count after setup:', dagreGraph.edges().length);
  
  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    console.log(`Node ${node.id} positioned at:`, nodeWithPosition);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 110,
        y: nodeWithPosition.y - 60,
      },
    };
  });

  console.log('🔧 Dagre layout - OUTPUT:', { nodes: layoutedNodes.length, edges: edges.length });
  console.log('Final edges being returned:', edges.slice(0, 3));

  return {
    nodes: layoutedNodes,
    edges, // Return the original edges unchanged
  };
};

// Helper function to determine edge type and styling for better visual separation
const getEdgeTypeAndStyle = (source, target, edgeIndex) => {
  // Use different types and colors based on connection patterns
  if (source.includes('_answer_')) {
    return {
      type: 'smoothstep',
      color: '#ff9800', // Orange for answer connections
      strokeWidth: 2,
      strokeDasharray: undefined
    };
  } else if (target.includes('thesis_')) {
    return {
      type: 'step',
      color: '#9c27b0', // Purple for thesis connections
      strokeWidth: 3,
      strokeDasharray: undefined
    };
  } else {
    // Question-to-question connections
    const types = ['straight', 'step', 'smoothstep'];
    const colors = ['#333', '#666', '#2196f3'];
    const typeIndex = edgeIndex % types.length;
    return {
      type: types[typeIndex],
      color: colors[typeIndex],
      strokeWidth: 2,
      strokeDasharray: typeIndex === 1 ? '8,4' : undefined // Dash pattern for step edges
    };
  }
};

// Keep the original function for backward compatibility
const getEdgeType = (source, target, edgeIndex) => {
  return getEdgeTypeAndStyle(source, target, edgeIndex).type;
};

// Helper function to get curved path offset to separate parallel edges
const getEdgeOffset = (sourceId, targetId, allEdges, currentIndex) => {
  // Find all edges between the same source and target
  const parallelEdges = allEdges.filter(edge => 
    (edge.source === sourceId && edge.target === targetId) ||
    (edge.source === targetId && edge.target === sourceId)
  );
  
  if (parallelEdges.length <= 1) return 0;
  
  // Calculate offset based on position in parallel edge group
  const edgePosition = parallelEdges.findIndex(edge => 
    edge.source === sourceId && edge.target === targetId
  );
  
  // Create offset pattern: 0, 20, -20, 40, -40, etc.
  if (edgePosition === 0) return 0;
  const magnitude = Math.ceil(edgePosition / 2) * 20;
  return edgePosition % 2 === 1 ? magnitude : -magnitude;
};

const QuestionLogicRepresentation = ({ 
  courseDesignState, 
  selectedQuestions = null, // Array of dataIDs or null for all
  filterByDate = null, // Filter by questionCourseDate (includes dependencies)
  onQuestionSelect = null,
  height = '600px',
  direction = 'TB', // 'TB', 'LR', 'BT', 'RL'
  showComplexityIndicators = true,
  enableMiniMap = true
}) => {
  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // Parse question options to create answer nodes
  const parseQuestionOptions = useCallback((questionOptions) => {
    if (!questionOptions || typeof questionOptions !== 'string') return [];
    
    return questionOptions
      .split('|||')
      .map((option, index) => {
        const trimmed = option.trim();
        // Extract number and text (e.g., "1. No one has any choice..." -> "1" and "No one has any choice...")
        const match = trimmed.match(/^(\d+)\.\s*(.+)$/);
        if (match) {
          return {
            number: parseInt(match[1]),
            text: match[2],
            positionIndex: index + 1, // 1-based position in the answer list
            premiseNumber: parseInt(match[1]) // The number that appears in the text
          };
        }
        return {
          number: index + 1,
          text: trimmed,
          positionIndex: index + 1, // 1-based position in the answer list
          premiseNumber: index + 1 // Default to position if no number in text
        };
      })
      .filter(option => option.text.length > 0);
  }, []);

  // Parse question logic to extract dependencies with answer specificity and thesis dependencies
  const parseDependencies = useCallback((questionLogic, allQuestions, allTheses) => {
    if (!questionLogic || typeof questionLogic !== 'string') return [];
    
    console.log('🔍 Parsing logic:', questionLogic);
    
    const dependencies = [];
    
    // Method 1: Look for (questionID, answerNum) pattern
    const answerRegex = /\(([^,()]+),\s*(\d+)\)/g;
    let match;
    while ((match = answerRegex.exec(questionLogic)) !== null) {
      const questionId = match[1].trim();
      const answerNumber = parseInt(match[2]); // This is the direct answer position (1-based)
      console.log('📋 Found reference pattern:', match[0], '-> questionID:', questionId, 'answer position:', answerNumber);
      
      // Check if this questionID exists in our questions
      const referencedQuestion = allQuestions.find(q => q.dataID === questionId);
      if (referencedQuestion) {
        dependencies.push({
          type: 'question',
          questionId: questionId,
          answerNumber: answerNumber, // Direct answer position
          pattern: match[0]
        });
        console.log('✅ Valid question dependency found:', questionId, 'answer position:', answerNumber);
      } else {
        console.log('❌ Question not found in dataset:', questionId);
      }
    }
    
    // Method 2: Check for thesis dependencies by looking for thesis dataText in logic
    if (allTheses && allTheses.length > 0) {
      console.log('🔄 Checking for thesis dependencies...');
      allTheses.forEach(thesis => {
        if (thesis.dataText && questionLogic.includes(thesis.dataText)) {
          dependencies.push({
            type: 'thesis',
            thesisId: thesis.dataID,
            thesisText: thesis.dataText,
            pattern: 'thesis_substring'
          });
          console.log('📜 Thesis dependency found:', thesis.dataText);
        }
      });
    }
    
    // Method 3: Fallback - Check if any question dataID appears in this logic string
    if (dependencies.filter(d => d.type === 'question').length === 0) {
      console.log('🔄 No regex matches, trying substring method...');
      allQuestions.forEach(question => {
        if (question.dataID && questionLogic.includes(question.dataID)) {
          dependencies.push({
            type: 'question',
            questionId: question.dataID,
            pattern: 'substring'
          });
          console.log('📎 Substring dependency found:', question.dataID);
        }
      });
    }
    
    console.log('📊 Total dependencies found:', dependencies.length);
    return dependencies;
  }, []);

  // Assess logic complexity
  const assessLogicComplexity = useCallback((questionLogic) => {
    if (!questionLogic) return 'none';
    
    const operators = (questionLogic.match(/&&|\|\||!/g) || []).length;
    const references = (questionLogic.match(/\([^,()]+,\s*\d+\)/g) || []).length;
    const total = operators + references;
    
    if (total >= 6) return 'high';
    if (total >= 3) return 'medium';
    if (total >= 1) return 'low';
    return 'none';
  }, []);

  // Find all dependencies (one level deep) for a set of questions
  const findDirectDependencies = useCallback((targetQuestions, allQuestions, allTheses) => {
    const dependencyIds = new Set();
    const thesisIds = new Set();
    
    console.log('🔍 Finding direct dependencies for', targetQuestions.length, 'questions');
    
    targetQuestions.forEach(question => {
      if (question.questionLogic && question.questionLogic.trim()) {
        const dependencies = parseDependencies(question.questionLogic, allQuestions, allTheses);
        dependencies.forEach(dep => {
          if (dep.type === 'question') {
            dependencyIds.add(dep.questionId);
            console.log(`📎 Added question dependency: ${dep.questionId} (from ${question.dataID})`);
          } else if (dep.type === 'thesis') {
            thesisIds.add(dep.thesisId);
            console.log(`📜 Added thesis dependency: ${dep.thesisId} (from ${question.dataID})`);
          }
        });
      }
    });
    
    console.log('📊 Found', dependencyIds.size, 'unique question dependencies and', thesisIds.size, 'unique thesis dependencies');
    return {
      questionIds: Array.from(dependencyIds),
      thesisIds: Array.from(thesisIds)
    };
  }, [parseDependencies]);

  // Generate nodes and edges from course design state
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    let questions = courseDesignState.filter(item => item.dataType === 'question');
    let theses = courseDesignState.filter(item => item.dataType === 'thesis');

    console.log('🔍 RAW DATA INSPECTION:');
    console.log('Total courseDesignState items:', courseDesignState.length);
    console.log('Questions found:', questions.length);
    console.log('Theses found:', theses.length);

    // Apply filterByDate logic if specified
    if (filterByDate) {
      console.log('📅 Filtering by date:', filterByDate);
      
      // Step 1: Get questions from the target date
      const questionsFromDate = questions.filter(q => q.questionCourseDate === filterByDate);
      console.log('Questions from target date:', questionsFromDate.length);
      
      // Step 2: Find their direct dependencies (both questions and theses)
      const directDependencies = findDirectDependencies(questionsFromDate, questions, theses);
      const dependencyQuestions = questions.filter(q => directDependencies.questionIds.includes(q.dataID));
      const dependencyTheses = theses.filter(t => directDependencies.thesisIds.includes(t.dataID));
      console.log('Direct dependency questions:', dependencyQuestions.length);
      console.log('Direct dependency theses:', dependencyTheses.length);
      
      // Step 3: Combine target date questions + their dependencies
      const combinedQuestionIds = new Set([
        ...questionsFromDate.map(q => q.dataID),
        ...directDependencies.questionIds
      ]);
      
      const combinedThesisIds = new Set(directDependencies.thesisIds);
      
      questions = questions.filter(q => combinedQuestionIds.has(q.dataID));
      theses = theses.filter(t => combinedThesisIds.has(t.dataID));
      console.log('Final filtered questions:', questions.length);
      console.log('Final filtered theses:', theses.length);
    }

    // Note: selectedQuestions should only affect styling, not which questions are displayed
    // Remove this filtering so all questions for the date are always shown
    // if (selectedQuestions !== null && selectedQuestions.length > 0) {
    //   questions = questions.filter(q => selectedQuestions.includes(q.dataID));
    // }

    console.log('Filtered questions:', questions.length);
    console.log('Sample question structure:', questions[0]);
    console.log('All question dataIDs:', questions.map(q => q.dataID));
    console.log('Questions with logic:', questions.filter(q => q.questionLogic && q.questionLogic.trim()).map(q => ({
      dataID: q.dataID,
      questionLogic: q.questionLogic
    })));
    console.log('Sample thesis structure:', theses[0]);
    console.log('All thesis dataIDs:', theses.map(t => t.dataID));
    console.log('All thesis dataTexts:', theses.map(t => t.dataText));

    const nodes = [];
    const edges = [];
    
    // Create a set of all node IDs for quick lookup (questions + answers + theses)
    const allNodeIds = new Set([
      ...questions.map(q => q.dataID),
      ...theses.map(t => t.dataID)
    ]);
    
    // Add answer node IDs to the set
    questions.forEach(question => {
      if (question.questionOptions && (question.questionType === 'Radio' || question.questionType === 'Checkbox')) {
        const answers = parseQuestionOptions(question.questionOptions);
        answers.forEach(answer => {
          allNodeIds.add(`${question.dataID}_answer_${answer.positionIndex}`);
        });
      }
    });

    // Create nodes first - both question nodes, answer nodes, and thesis nodes
    questions.forEach((question) => {
      // Ensure we have a valid dataID
      if (!question.dataID) {
        console.warn('Question missing dataID:', question);
        return;
      }

      const hasLogic = Boolean(question.questionLogic && question.questionLogic.trim());
      const logicComplexity = showComplexityIndicators ? assessLogicComplexity(question.questionLogic) : 'none';
      
      // Determine if this question is from target date or a dependency
      const isFromTargetDate = filterByDate ? question.questionCourseDate === filterByDate : false;
      const isDependency = filterByDate ? question.questionCourseDate !== filterByDate : false;
      
      // Create the main question node
      nodes.push({
        id: question.dataID,
        type: 'questionNode',
        position: { x: 0, y: 0 }, // Will be set by Dagre
        data: {
          label: question.dataText || `Question ${question.dataID}`,
          questionType: question.questionType || 'Unknown',
          hasLogic,
          logicComplexity,
          isSelected: selectedQuestions ? selectedQuestions.includes(question.dataID) : (selectedNodeId === question.dataID),
          isFromTargetDate,
          isDependency,
          isAnswer: false,
          fullQuestion: question
        },
      });

      // Create answer nodes for multiple choice questions
      if (question.questionOptions && (question.questionType === 'Radio' || question.questionType === 'Checkbox')) {
        const answers = parseQuestionOptions(question.questionOptions);
        answers.forEach((answer) => {
          const answerNodeId = `${question.dataID}_answer_${answer.positionIndex}`;
          nodes.push({
            id: answerNodeId,
            type: 'questionNode',
            position: { x: 0, y: 0 }, // Will be set by Dagre
            data: {
              label: answer.text,
              questionType: question.questionType,
              hasLogic: false,
              logicComplexity: 'none',
              isSelected: selectedQuestions ? selectedQuestions.includes(question.dataID) : (selectedNodeId === answerNodeId),
              isFromTargetDate,
              isDependency,
              isAnswer: true,
              answerNumber: answer.positionIndex, // Use position index
              premiseNumber: answer.premiseNumber, // Keep premise number for display
              parentQuestionId: question.dataID,
              fullQuestion: question
            },
          });
        });
      }
    });

    // Create thesis nodes
    theses.forEach((thesis) => {
      // Ensure we have a valid dataID
      if (!thesis.dataID) {
        console.warn('Thesis missing dataID:', thesis);
        return;
      }

      nodes.push({
        id: thesis.dataID,
        type: 'questionNode',
        position: { x: 0, y: 0 }, // Will be set by Dagre
        data: {
          label: thesis.questionOptions || thesis.dataText || `Thesis ${thesis.dataID}`,
          questionType: 'Thesis',
          hasLogic: false,
          logicComplexity: 'none',
          isSelected: selectedNodeId === thesis.dataID,
          isFromTargetDate: false, // Theses don't have dates
          isDependency: true, // Theses are always dependencies
          isAnswer: false,
          isThesis: true,
          thesisAbbreviation: thesis.dataText,
          fullThesis: thesis
        },
      });
    });

    // Create edges from questions to their answer nodes
    questions.forEach((question) => {
      if (question.questionOptions && (question.questionType === 'Radio' || question.questionType === 'Checkbox')) {
        const answers = parseQuestionOptions(question.questionOptions);
        answers.forEach((answer, answerIndex) => {
          const answerNodeId = `${question.dataID}_answer_${answer.positionIndex}`;
          if (allNodeIds.has(answerNodeId)) {
            const edgeType = getEdgeType(question.dataID, answerNodeId, answerIndex);
            const edge = {
              id: `${question.dataID}-${answerNodeId}`,
              source: question.dataID,
              sourceHandle: 'output',
              target: answerNodeId,
              targetHandle: 'input',
              label: '',
              type: edgeType,
              animated: selectedNodeId === question.dataID || selectedNodeId === answerNodeId,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 16,
                height: 16,
                color: '#666',
              },
              style: {
                strokeWidth: 2,
                stroke: '#666',
                strokeDasharray: '5,5', // Dashed line to distinguish from dependency edges
              },
              // Add curved path for smoothstep edges to separate them
              ...(edgeType === 'smoothstep' && {
                pathOptions: {
                  offset: answerIndex * 10, // Offset parallel edges
                  borderRadius: 20
                }
              })
            };
            edges.push(edge);
            console.log('✅ Created question-to-answer edge:', question.dataID, '->', answerNodeId);
          }
        });
      }
    });

    // Create edges only after all nodes are created
    questions.forEach((question) => {
      const hasLogic = Boolean(question.questionLogic && question.questionLogic.trim());
      
      if (hasLogic) {
        console.log(`Processing question ${question.dataID} with logic: "${question.questionLogic}"`);
        const dependencies = parseDependencies(question.questionLogic, questions, theses);
        console.log(`Found ${dependencies.length} dependencies for question ${question.dataID}:`, dependencies);
        
        dependencies.forEach((dep, depIndex) => {
          console.log(`Processing dependency ${depIndex + 1}/${dependencies.length}:`, dep);
          
          if (dep.type === 'thesis') {
            // Handle thesis dependencies
            if (allNodeIds.has(dep.thesisId)) {
              const edge = {
                id: `${dep.thesisId}-${question.dataID}-${depIndex}`,
                source: dep.thesisId,
                sourceHandle: 'output',
                target: question.dataID,
                targetHandle: 'input',
                label: dep.thesisText,
                type: 'step', // Use step type for thesis connections to distinguish them
                animated: selectedNodeId === question.dataID || selectedNodeId === dep.thesisId,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 20,
                  height: 20,
                  color: '#333',
                },
                style: {
                  strokeWidth: selectedNodeId === question.dataID || selectedNodeId === dep.thesisId ? 4 : 3,
                  stroke: selectedNodeId === question.dataID || selectedNodeId === dep.thesisId ? '#1976d2' : '#9c27b0', // Purple for thesis dependencies
                },
                labelStyle: {
                  fontSize: '11px',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                },
              };
              edges.push(edge);
              console.log('✅ Successfully created thesis edge:', dep.thesisId, '->', question.dataID);
            } else {
              console.warn(`❌ Thesis node not found: ${dep.thesisId}`);
            }
          } else if (dep.type === 'question') {
            // Handle question dependencies (existing logic)
            
            // Validate that both source and target exist
            if (!dep.questionId || !question.dataID) {
              console.warn('Invalid edge data:', { dep, questionId: question.dataID });
              return;
            }

            console.log(`Checking if nodes exist - Source: ${dep.questionId} (exists: ${allNodeIds.has(dep.questionId)}), Target: ${question.dataID} (exists: ${allNodeIds.has(question.dataID)})`);

            // Create edges from dependencies to specific answers when specified
            if (dep.answerNumber) {
              const sourceAnswerNodeId = `${dep.questionId}_answer_${dep.answerNumber}`;
              if (allNodeIds.has(sourceAnswerNodeId)) {
                const edgeType = getEdgeType(sourceAnswerNodeId, question.dataID, depIndex);
                const edge = {
                  id: `${sourceAnswerNodeId}-${question.dataID}-${depIndex}`,
                  source: sourceAnswerNodeId,
                  sourceHandle: 'output',
                  target: question.dataID,
                  targetHandle: 'input',
                  label: '',
                  type: edgeType,
                  animated: selectedNodeId === question.dataID || selectedNodeId === sourceAnswerNodeId,
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#333',
                  },
                  style: {
                    strokeWidth: selectedNodeId === question.dataID || selectedNodeId === sourceAnswerNodeId ? 4 : 3,
                    stroke: selectedNodeId === question.dataID || selectedNodeId === sourceAnswerNodeId ? '#1976d2' : '#ff9800',
                  },
                  labelStyle: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  },
                };
                edges.push(edge);
                console.log('✅ Successfully created answer-specific edge:', sourceAnswerNodeId, '->', question.dataID);
              } else {
                console.warn(`❌ Answer node not found: ${sourceAnswerNodeId}`);
              }
            } else {
              // Fallback to question-level dependency
              if (allNodeIds.has(dep.questionId) && allNodeIds.has(question.dataID)) {
                const edge = {
                  id: `${dep.questionId}-${question.dataID}-${depIndex}`,
                  source: dep.questionId,
                  sourceHandle: 'output',
                  target: question.dataID,
                  targetHandle: 'input',
                  label: 'depends on',
                  type: 'straight', // Use straight lines for question-to-question to distinguish from answer dependencies
                  animated: selectedNodeId === question.dataID || selectedNodeId === dep.questionId,
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#333',
                  },
                  style: {
                    strokeWidth: selectedNodeId === question.dataID || selectedNodeId === dep.questionId ? 4 : 3,
                    stroke: selectedNodeId === question.dataID || selectedNodeId === dep.questionId ? '#1976d2' : '#333',
                  },
                  labelStyle: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  },
                };
                edges.push(edge);
                console.log('✅ Successfully created question-level edge:', dep.questionId, '->', question.dataID);
              } else {
                console.warn(`❌ Skipping edge - missing node. Source: ${dep.questionId} (exists: ${allNodeIds.has(dep.questionId)}), Target: ${question.dataID} (exists: ${allNodeIds.has(question.dataID)})`);
              }
            }
          }
        });
      } else {
        console.log(`Question ${question.dataID} has no logic`);
      }
    });

    // Process thesis dependencies
    theses.forEach((thesis) => {
      const hasLogic = Boolean(thesis.questionLogic && thesis.questionLogic.trim());
      
      if (hasLogic) {
        console.log(`Processing thesis ${thesis.dataID} with logic: "${thesis.questionLogic}"`);
        const dependencies = parseDependencies(thesis.questionLogic, questions, theses);
        console.log(`Found ${dependencies.length} dependencies for thesis ${thesis.dataID}:`, dependencies);
        
        dependencies.forEach((dep, depIndex) => {
          console.log(`Processing dependency ${depIndex + 1}/${dependencies.length}:`, dep);
          
          if (dep.type === 'thesis') {
            // Handle thesis-to-thesis dependencies
            if (allNodeIds.has(dep.thesisId)) {
              const edge = {
                id: `${dep.thesisId}-${thesis.dataID}-${depIndex}`,
                source: dep.thesisId,
                sourceHandle: 'output',
                target: thesis.dataID,
                targetHandle: 'input',
                label: dep.thesisText,
                type: 'smoothstep',
                animated: selectedNodeId === thesis.dataID || selectedNodeId === dep.thesisId,
                markerEnd: {
                  type: MarkerType.ArrowClosed,
                  width: 20,
                  height: 20,
                  color: '#333',
                },
                style: {
                  strokeWidth: selectedNodeId === thesis.dataID || selectedNodeId === dep.thesisId ? 4 : 3,
                  stroke: selectedNodeId === thesis.dataID || selectedNodeId === dep.thesisId ? '#1976d2' : '#9c27b0', // Purple for thesis dependencies
                },
                labelStyle: {
                  fontSize: '11px',
                  fontWeight: 'bold',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                  padding: '4px 6px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                },
              };
              edges.push(edge);
              console.log('✅ Successfully created thesis-to-thesis edge:', dep.thesisId, '->', thesis.dataID);
            } else {
              console.warn(`❌ Thesis node not found: ${dep.thesisId}`);
            }
          } else if (dep.type === 'question') {
            // Handle thesis dependencies on questions
            
            // Validate that both source and target exist
            if (!dep.questionId || !thesis.dataID) {
              console.warn('Invalid edge data:', { dep, thesisId: thesis.dataID });
              return;
            }

            console.log(`Checking if nodes exist - Source: ${dep.questionId} (exists: ${allNodeIds.has(dep.questionId)}), Target: ${thesis.dataID} (exists: ${allNodeIds.has(thesis.dataID)})`);

            // Create edges from dependencies to specific answers when specified
            if (dep.answerNumber) {
              const sourceAnswerNodeId = `${dep.questionId}_answer_${dep.answerNumber}`;
              if (allNodeIds.has(sourceAnswerNodeId)) {
                const edge = {
                  id: `${sourceAnswerNodeId}-${thesis.dataID}-${depIndex}`,
                  source: sourceAnswerNodeId,
                  sourceHandle: 'output',
                  target: thesis.dataID,
                  targetHandle: 'input',
                  label: '',
                  type: 'smoothstep',
                  animated: selectedNodeId === thesis.dataID || selectedNodeId === sourceAnswerNodeId,
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#333',
                  },
                  style: {
                    strokeWidth: selectedNodeId === thesis.dataID || selectedNodeId === sourceAnswerNodeId ? 4 : 3,
                    stroke: selectedNodeId === thesis.dataID || selectedNodeId === sourceAnswerNodeId ? '#1976d2' : '#ff9800',
                  },
                  labelStyle: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  },
                };
                edges.push(edge);
                console.log('✅ Successfully created answer-to-thesis edge:', sourceAnswerNodeId, '->', thesis.dataID);
              } else {
                console.warn(`❌ Answer node not found: ${sourceAnswerNodeId}`);
              }
            } else {
              // Fallback to question-level dependency
              if (allNodeIds.has(dep.questionId) && allNodeIds.has(thesis.dataID)) {
                const edge = {
                  id: `${dep.questionId}-${thesis.dataID}-${depIndex}`,
                  source: dep.questionId,
                  sourceHandle: 'output',
                  target: thesis.dataID,
                  targetHandle: 'input',
                  label: 'depends on',
                  type: 'smoothstep',
                  animated: selectedNodeId === thesis.dataID || selectedNodeId === dep.questionId,
                  markerEnd: {
                    type: MarkerType.ArrowClosed,
                    width: 20,
                    height: 20,
                    color: '#333',
                  },
                  style: {
                    strokeWidth: selectedNodeId === thesis.dataID || selectedNodeId === dep.questionId ? 4 : 3,
                    stroke: selectedNodeId === thesis.dataID || selectedNodeId === dep.questionId ? '#1976d2' : '#333',
                  },
                  labelStyle: {
                    fontSize: '11px',
                    fontWeight: 'bold',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    padding: '4px 6px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  },
                };
                edges.push(edge);
                console.log('✅ Successfully created question-to-thesis edge:', dep.questionId, '->', thesis.dataID);
              } else {
                console.warn(`❌ Skipping edge - missing node. Source: ${dep.questionId} (exists: ${allNodeIds.has(dep.questionId)}), Target: ${thesis.dataID} (exists: ${allNodeIds.has(thesis.dataID)})`);
              }
            }
          }
        });
      } else {
        console.log(`Thesis ${thesis.dataID} has no logic`);
      }
    });

    console.log(`Created ${nodes.length} nodes and ${edges.length} edges`);
    
    // Enhanced edge debugging
    console.log('🔗 EDGE DETAILS:');
    console.log('First 5 edges:', edges.slice(0, 5));
    edges.slice(0, 5).forEach((edge, i) => {
      console.log(`Edge ${i + 1}:`, {
        id: edge.id,
        source: edge.source,
        target: edge.target,
        type: edge.type,
        markerEnd: edge.markerEnd
      });
    });
    
    // Log some sample question logic to help debug
    const questionsWithLogic = questions.filter(q => q.questionLogic && q.questionLogic.trim());
    console.log('Sample questions with logic:');
    questionsWithLogic.slice(0, 3).forEach(q => {
      console.log(`  ${q.dataID}: "${q.questionLogic}"`);
    });

    // Apply Dagre layout
    const layoutResult = getLayoutedElements(nodes, edges, direction);
    console.log('🎯 DAGRE LAYOUT RESULT:');
    console.log('Nodes after layout:', layoutResult.nodes.length);
    console.log('Edges after layout:', layoutResult.edges.length);
    console.log('Sample positioned nodes:', layoutResult.nodes.slice(0, 3).map(n => ({
      id: n.id,
      position: n.position
    })));
    console.log('Sample edges after layout:', layoutResult.edges.slice(0, 3));
    
    return layoutResult;
  }, [courseDesignState, selectedQuestions, filterByDate, selectedNodeId, parseDependencies, parseQuestionOptions, findDirectDependencies, assessLogicComplexity, direction, showComplexityIndicators]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update React Flow state when initial data changes (e.g., when filterByDate changes)
  React.useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  // Debug what React Flow actually receives
  React.useEffect(() => {
    console.log('🎨 REACT FLOW STATE UPDATE:');
    console.log('Nodes in React Flow state:', nodes.length);
    console.log('Edges in React Flow state:', edges.length);
    console.log('Sample React Flow edges:', edges.slice(0, 3));
    
    if (edges.length === 0 && initialEdges.length > 0) {
      console.log('🚨 WARNING: Initial edges were lost! Initial had:', initialEdges.length);
      console.log('Initial edges sample:', initialEdges.slice(0, 3));
    }
  }, [nodes, edges, initialEdges]);

  const onNodeClick = useCallback((event, node) => {
    // Don't allow clicking on answer nodes - they should not be individually selectable
    if (node.data.isAnswer) {
      return;
    }
    
    // If external selectedQuestions state is being used (sharing mode), 
    // don't manage local selectedNodeId state - let the parent handle it
    if (!selectedQuestions) {
      setSelectedNodeId(node.id === selectedNodeId ? null : node.id);
    }
    
    if (onQuestionSelect) {
      onQuestionSelect(node.data.fullQuestion || node.data.fullThesis);
    }
  }, [onQuestionSelect, selectedNodeId, selectedQuestions]);

  // Update nodes when selection changes
  React.useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => {
        let isSelected;
        if (node.data.isAnswer) {
          // For answer nodes, use parent question's selection state
          const parentQuestionId = node.data.parentQuestionId;
          isSelected = selectedQuestions ? selectedQuestions.includes(parentQuestionId) : (selectedNodeId === node.id);
        } else {
          // For question and thesis nodes
          isSelected = selectedQuestions ? selectedQuestions.includes(node.id) : (selectedNodeId === node.id);
        }
        
        return {
          ...node,
          data: {
            ...node.data,
            isSelected,
          },
        };
      })
    );
  }, [selectedNodeId, selectedQuestions, setNodes]);

  // Update edges when selection changes
  React.useEffect(() => {
    setEdges((eds) =>
      eds.map((edge) => ({
        ...edge,
        animated: selectedNodeId === edge.source || selectedNodeId === edge.target,
        style: {
          ...edge.style,
          strokeWidth: selectedNodeId === edge.source || selectedNodeId === edge.target ? 3 : 2,
          stroke: selectedNodeId === edge.source || selectedNodeId === edge.target ? '#1976d2' : '#555',
        },
      }))
    );
  }, [selectedNodeId, setEdges]);

  const clearSelection = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  return (
    <div style={{ width: '100%', height, position: 'relative' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        onPaneClick={clearSelection}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.1 }}
        attributionPosition="bottom-left"
        connectionLineStyle={{ stroke: '#ddd', strokeWidth: 2 }}
        defaultEdgeOptions={{
          type: 'smoothstep',
          animated: false,
          style: { strokeWidth: 2 }
        }}
        snapToGrid={true}
        snapGrid={[20, 20]}
        connectionMode="loose"
      >
        <Controls />
        {enableMiniMap && <MiniMap />}
        <Background variant="dots" gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

export default QuestionLogicRepresentation;
